import { useInfiniteQuery } from "@tanstack/react-query";
import {
  permissionService,
  PermissionFilters,
  PermissionsResponse,
} from "../api/services/permissionService";
import type { Permission } from "../schemas/permissionSchema";
import { usePermissions } from "./usePermissions";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type PermissionsInfiniteFilters = Omit<
  PermissionFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const usePermissionsInfinite = (
  filters: PermissionsInfiniteFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = usePermissions(undefined, { enabled: false });

  const query = useInfiniteQuery<PermissionsResponse, Error>({
    queryKey: ["permissions", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      permissionService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const permissions: Permission[] =
    query.data?.pages.flatMap((p) => p.data) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    permissions,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
